import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { MODELS, MODEL_KEYS, ModelChip, ModelBadge } from "../components/ModelBadge";
import Markdown from "../components/Markdown";
import { BookOpen, Search, Sparkles, Globe } from "lucide-react";

export default function Research() {
  const [params, setParams] = useSearchParams();
  const convId = params.get("c");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(["gpt-4o-mini", "claude-haiku-4-5", "gemini-2-5-flash-lite", "deepseek-chat"]);
  const [webSearch, setWebSearch] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null); // {responses:[], synthesis:{}}
  const [topic, setTopic] = useState("");

  useEffect(() => {
    if (convId) {
      (async () => {
        try {
          const { data: c } = await api.get(`/conversations/${convId}`);
          const msgs = c.messages || [];
          const userMsg = msgs.find((m) => m.role === "user");
          if (userMsg) setTopic(userMsg.content);
          const assistants = msgs.filter((m) => m.role === "assistant");
          const synth = assistants.find((m) => m.content?.startsWith("**📋 Synthesis:"));
          const responses = assistants
            .filter((m) => m !== synth)
            .map((m) => ({ model: m.model, content: m.content }));
          setData({ responses, synthesis: synth ? { content: synth.content.replace(/^\*\*📋 Synthesis:\*\*\s*/, "") } : null });
        } catch {}
      })();
    }
  }, [convId]);

  const toggle = (k) => setSelected((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  const research = async () => {
    if (!query.trim() || busy || selected.length === 0) return;
    setBusy(true);
    setTopic(query);
    setData(null);
    try {
      const { data: res } = await api.post("/research", { query, models: selected, web_search: webSearch });
      setData(res);
      if (res.conversation_id) setParams({ c: res.conversation_id });
    } catch (e) {
      setData({ responses: [], synthesis: { error: e.response?.data?.detail || e.message } });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-screen" data-testid="research-page">
      <header className="px-6 py-4 border-b-2 border-slate-900 bg-white">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={22} strokeWidth={2.5} />
          <h1 className="font-heading text-2xl font-bold">Research</h1>
          <span className="text-sm text-slate-500">— multi-model briefing</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-3" data-testid="research-models">
          {MODEL_KEYS.map((k) => (
            <ModelChip key={k} id={k} selected={selected.includes(k)} onClick={() => toggle(k)} />
          ))}
          <button
            data-testid="research-web-toggle"
            onClick={() => setWebSearch((w) => !w)}
            className={`flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-heading font-bold border-2 transition-all ${
              webSearch
                ? "bg-emerald-500 text-white border-emerald-700 shadow-brut-sm"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"
            }`}
          >
            <Globe size={16} strokeWidth={2.5} />
            Web {webSearch ? "ON" : "OFF"}
          </button>
        </div>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              data-testid="research-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") research(); }}
              className="brut-input pl-9"
              placeholder="What do you want to research? e.g., 'How do mixture-of-experts models work?'"
            />
          </div>
          <button
            data-testid="research-go-btn"
            onClick={research}
            disabled={busy || !query.trim() || selected.length === 0}
            className="brut-btn"
          >
            {busy ? "Researching…" : "Research"}
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          {!data && !busy && (
            <div className="text-center py-20 max-w-lg mx-auto" data-testid="research-empty">
              <div className="text-6xl mb-3 float-y inline-block">🔬</div>
              <h2 className="font-heading text-2xl font-bold mb-2">Ask. Compare. Synthesize.</h2>
              <p className="text-slate-500">All 4 models will research your topic in parallel and produce a final synthesized briefing.</p>
            </div>
          )}

          {topic && (
            <div className="brut-card p-4 text-center font-semibold" data-testid="research-topic">
              <div className="text-xs uppercase font-bold tracking-widest text-slate-500 mb-1">Topic</div>
              {topic}
            </div>
          )}

          {busy && (
            <div className="flex items-center justify-center gap-2 py-8 text-slate-500">
              <Sparkles className="animate-pulse" size={20} />
              <span className="font-heading font-semibold">Models are thinking…</span>
            </div>
          )}

          {data?.synthesis && (
            <div className="brut-card p-6 bg-amber-50" data-testid="research-synthesis">
              <div className="flex items-center gap-2 mb-3">
                <div className="px-3 py-1 rounded-full bg-slate-900 text-white text-xs font-heading font-bold uppercase tracking-wider">📋 Synthesis</div>
              </div>
              {data.synthesis.error ? (
                <div className="text-red-600">⚠️ {data.synthesis.error}</div>
              ) : (
                <>
                  <Markdown text={data.synthesis.content || ""} />
                  <Citations citations={data.synthesis.citations} />
                </>
              )}
            </div>
          )}

          {data?.responses?.length > 0 && (
            <div>
              <h3 className="font-heading text-lg font-bold mb-3">Per-model findings</h3>
              <div className="grid md:grid-cols-2 gap-4" data-testid="research-grid">
                {data.responses.map((r) => {
                  const m = MODELS[r.model];
                  if (!m) return null;
                  return (
                    <div
                      key={r.model}
                      className="border-2 rounded-2xl overflow-hidden"
                      style={{ borderColor: m.border }}
                    >
                      <div
                        className="px-4 py-2 border-b-2 flex items-center gap-2"
                        style={{ backgroundColor: m.bg, borderColor: m.border, color: m.border }}
                      >
                        <ModelBadge id={r.model} size="sm" />
                        <span className="text-xs font-bold uppercase tracking-wider">{m.personality}</span>
                      </div>
                      <div className="p-4 text-sm bg-white">
                        {r.error ? (
                          <div className="text-red-600">⚠️ {r.error}</div>
                        ) : (
                          <>
                            <Markdown text={r.content || ""} />
                            <Citations citations={r.citations} />
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Citations({ citations }) {
  if (!citations || citations.length === 0) return null;
  // Normalize OpenRouter annotation format
  const items = citations
    .map((c) => {
      if (c?.url_citation) return { url: c.url_citation.url, title: c.url_citation.title || c.url_citation.url };
      if (c?.url) return { url: c.url, title: c.title || c.url };
      return null;
    })
    .filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="mt-3 pt-3 border-t border-slate-200" data-testid="citations">
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5 flex items-center gap-1">
        🌐 Sources
      </div>
      <ol className="space-y-1">
        {items.slice(0, 8).map((c, i) => (
          <li key={i} className="text-xs">
            <a
              href={c.url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline font-semibold line-clamp-1"
              title={c.url}
            >
              [{i + 1}] {c.title}
            </a>
          </li>
        ))}
      </ol>
    </div>
  );
}
